import React, {useEffect, useRef} from 'react';
import { ISearchRequest } from '../../models/Search';
import { PrimaryButton, DefaultButton, Image, IconButton, TextField, Icon, Spinner, SpinnerSize, PanelType, Checkbox, Link } from '@fluentui/react';
import {IProductBundleRef} from "../../models/ProductBundles";
import {useGetProductBundleRefsQuery, usePostProductBundleMutation} from "../../store/endpoints/ProductBundleEndpoints";
import {
    useAcceptBundleSuggestionMutation,
    useDismissBundleSuggestionMutation,
    useGenerateBundleSuggestionsMutation,
    useGetBundleSuggestionsQuery
} from "../../store/endpoints/BundleSuggestionEndpoints";
import {useLazyResolveRecordUrlQuery} from "../../store/endpoints/RecordLinksEndpoints";
import useBundleSuggestionsAvailable from "./useBundleSuggestionsAvailable";
import {IAcceptBundleSuggestionRequest, IBundleSuggestion, IBundleSuggestionGroup, IBundleSuggestionItem, IDismissBundleSuggestionRequest} from "../../models/BundleSuggestions";
import StorefrontBundleModal from "./StorefrontBundleModal";
import DismissBundleSuggestionDialog from "./DismissBundleSuggestionDialog";
import PanelWrapper from "../Common/PanelWrapper";
import {BUNDLE_ICON, PLACEHOLDER_IMAGE} from "../../Constants";
import ToggleField from "../Common/ToggleField";
import BundleCardExtras from "./BundleCardExtras";
import {toast} from "react-toastify";
import {useDispatch} from "react-redux";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {hasDraggedProducts, IDraggedProduct, readDraggedProducts} from "../../logic/productDrag";
import {addProductsToBundle, toDraggedProducts} from "../../logic/addProductsToBundle";

const MAX_ENRICHMENT_POLLS = 24; // ~2 minutes at the 5s poll interval, so a stalled worker doesn't poll forever
const RECORD_TYPE_OPPORTUNITY = 'Opportunity';

const formatCurrency = (value?: number): string =>
    value == null ? '' : value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});

// One row of the panel's editable items table - a suggestion item or a context item, unified so both
// can share the same table/checkbox/qty/markup handling.
interface IEditableBundleItemRow {
    itemKey: string;
    isContext: boolean;
    displayName: string;
    resolved: boolean;
    inStock?: boolean;
    unitCost?: number;
    unitPrice?: number;
    include: boolean;
    quantity: number;
    markup: number;
    // The item's historical margin, kept so the row's markup can be re-derived against a different
    // bundle-level markup when the user turns per-item markup on after typing one.
    marginPercent?: number;
    // The user has touched this row (include, quantity or markup). Rows the LLM enrichment pass changes
    // under an open panel are rebuilt from the new data, but an edited row keeps what the user set.
    userModified?: boolean;
    // The user typed a value into this row's markup box, so it is never reseeded from the bundle markup.
    markupEdited?: boolean;
    // The suggestion marks this item optional (or it is an LLM-promoted context item, which is an offer
    // rather than a purchase). Shown as a small tag so the user can see it without a Role column.
    optional: boolean;
    // Set when this row is a member of a "pick one" group (role 'groupOption' on the wire). These rows
    // render in their group's own table with a radio in the control column instead of a checkbox; include
    // is not driven by a checkbox for them (it's derived from the group's radio selection at accept time),
    // but quantity/markup are still editable exactly like a regular row.
    groupId?: string;
}

// Back out a whole-number markup from a historical margin percent: markup = m/(1-m)*100, m = margin/100.
// Falls back to the bundle-level markup when margin is missing or out of range.
const markupFromMargin = (marginPercent: number | undefined, fallback: number): number => {
    if (marginPercent == null) return fallback;
    const m = marginPercent / 100;
    if (m <= 0 || m >= 1) return fallback;
    return Math.round((m / (1 - m)) * 100);
};

// Everything about a suggestion that the panel's editable rows are derived from, flattened to a string.
// The background LLM enrichment pass can promote a context item into the bundle (and re-price it) while
// the panel is already open, so the rows are rebuilt whenever this changes rather than only when a
// different suggestion is opened. The id leads so a change of suggestion is detectable from it too.
const rowsSignature = (suggestion?: IBundleSuggestion): string => {
    if (!suggestion) return '';
    const itemPart = (items?: IBundleSuggestionItem[]) =>
        (items ?? []).map(i => `${i.itemKey}:${i.role}:${i.optionality ?? ''}:${i.groupId ?? ''}:${i.resolvedProductId ?? ''}`).join('|');
    const groupPart = (suggestion.groups ?? []).map(g => `${g.id}:${g.itemKeys.join(',')}:${g.defaultItemKey}`).join('|');
    return [suggestion.id, itemPart(suggestion.items), itemPart(suggestion.contextItems), groupPart, Math.round(suggestion.suggestedMarkup ?? 0)].join('#');
};

interface IProductBundleSearchTableProps {
    /** When set, opening a bundle's editor is the page's job (the search page docks it beside the results). */
    onOpenBundle?: (bundle: IProductBundleRef) => void;
    searchCriteria: ISearchRequest;
    showResults?: boolean;
    onLoading?: (loading: boolean) => void;
    onResultsCountChange?: (count: number) => void;
    onSelectedBundleChange?: (bundle: IProductBundleRef | undefined) => void;
    onCreateNewBundle?: (bundle: IProductBundleRef | undefined) => void;
    onHide?: () => void;
    /** The grid's current selection: "Add selected to bundle" adds these to the selected bundle. */
    selectedProducts?: INormalizedProduct[];
    /** Height of the strip while pinned (0 when not pinned), so the page can push its sticky header down. */
    onPinnedHeightChange?: (height: number) => void;
    /** Pinned strip: distance from the viewport's right edge to stop at (a docked panel beside the results). */
    pinnedRightInset?: number;
}

const PIN_STORAGE_KEY = 'ac.bundleStrip.pinned';
/** Whether the user last left the search page's bundle strip pinned (remembered per browser). */
export const loadPinned = (): boolean => { try { return window.localStorage.getItem(PIN_STORAGE_KEY) === '1'; } catch { return false; } };
const savePinned = (pinned: boolean) => { try { window.localStorage.setItem(PIN_STORAGE_KEY, pinned ? '1' : '0'); } catch { /* per-viewer convenience */ } };
/** The app header the pinned strip sticks under (the results header sticks at the same offset). */
const HEADER_HEIGHT = 48;

const ProductBundleSearchTable: React.FC<IProductBundleSearchTableProps> = (props) => {
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedBundle, setSelectedBundle] = React.useState<IProductBundleRef | undefined>();
    const [detailsBundle, setDetailsBundle] = React.useState<IProductBundleRef | undefined>();
    const dispatch = useDispatch();
    // Pinned: the strip sticks under the app header as a row of mini cards so bundles stay in reach while
    // scrolling the results. Remembered per browser.
    const [pinned, setPinned] = React.useState<boolean>(() => loadPinned());
    const togglePinned = () => setPinned((p) => { savePinned(!p); return !p; });
    // Closing the strip (the Bundles toggle) also unpins it, so it does not reappear pinned on the next load.
    useEffect(() => {
        if (!props.showResults && pinned) { setPinned(false); savePinned(false); }
    }, [props.showResults, pinned]);
    // Pinned is always the compact row of mini cards, whether it is sitting in place or stuck under the header,
    // so nothing reshuffles as the page scrolls.
    const compact = pinned;
    const stripRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const node = stripRef.current;
        if (!compact || !props.showResults || !node) { props.onPinnedHeightChange?.(0); return; }
        const report = () => props.onPinnedHeightChange?.(node.getBoundingClientRect().height);
        report();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(report);
        observer.observe(node);
        return () => { observer.disconnect(); props.onPinnedHeightChange?.(0); };
    }, [compact, props.showResults]);
    // Drop target feedback: the bundle card the pointer is over with products in tow.
    const [dragOverBundleId, setDragOverBundleId] = React.useState<string | undefined>();
    const [addingToBundleId, setAddingToBundleId] = React.useState<string | undefined>();
    const addToBundle = async (bundle: IProductBundleRef, refs: IDraggedProduct[]) => {
        if (!bundle.id) return;
        setAddingToBundleId(bundle.id);
        try { await addProductsToBundle(dispatch, bundle.id, bundle.name, refs); }
        finally { setAddingToBundleId(undefined); }
    };
    const dropHandlers = (bundle: IProductBundleRef) => ({
        onDragOver: (e: React.DragEvent) => {
            if (!hasDraggedProducts(e.dataTransfer)) return;
            e.preventDefault(); e.dataTransfer.dropEffect = 'copy';
            if (dragOverBundleId !== bundle.id) setDragOverBundleId(bundle.id);
        },
        onDragLeave: (e: React.DragEvent) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null) && dragOverBundleId === bundle.id) setDragOverBundleId(undefined);
        },
        onDrop: (e: React.DragEvent) => {
            if (!hasDraggedProducts(e.dataTransfer)) return;
            e.preventDefault(); e.stopPropagation(); setDragOverBundleId(undefined);
            void addToBundle(bundle, readDraggedProducts(e.dataTransfer));
        },
    });
    const selectedRefs = toDraggedProducts(props.selectedProducts ?? []);
    // The page may take over the editor (the search page docks it beside the grid); otherwise it opens here.
    const openDetails = (bundle: IProductBundleRef) => props.onOpenBundle ? props.onOpenBundle(bundle) : setDetailsBundle(bundle);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [leftHover, setLeftHover] = React.useState(false);
    const [rightHover, setRightHover] = React.useState(false);
    const [keyword, setKeyword] = React.useState<string>('');
    const [debouncedKeyword, setDebouncedKeyword] = React.useState<string>('');
    const [createHover, setCreateHover] = React.useState(false);
    const [creating, setCreating] = React.useState(false)
    const [showAllBundles, setShowAllBundles] = React.useState(false);
    // Only the id is held here - the suggestion itself is read back out of the polled run below, so an
    // open panel follows the background enrichment instead of showing (and accepting) a frozen snapshot.
    const [detailsSuggestionId, setDetailsSuggestionId] = React.useState<string | undefined>();
    const [acceptingSuggestionId, setAcceptingSuggestionId] = React.useState<string | undefined>();
    const [dismissingSuggestionId, setDismissingSuggestionId] = React.useState<string | undefined>();
    // The suggestion currently being dismissed via the reason dialog, plus whether it was opened from
    // the details panel (which closes on confirm) or the card's X icon (which doesn't).
    const [dismissDialogTarget, setDismissDialogTarget] = React.useState<{suggestion: IBundleSuggestion, fromPanel: boolean} | undefined>();
    const [itemRows, setItemRows] = React.useState<IEditableBundleItemRow[]>([]);
    const [useItemMarkup, setUseItemMarkup] = React.useState(false);
    // Radio selection per group, keyed by group id. "Use grouped optionality" defaults ON (undefined on
    // the wire means true too, but the toggle needs a concrete boolean to render).
    const [groupSelections, setGroupSelections] = React.useState<Record<string, string>>({});
    const [useGroupedOptionality, setUseGroupedOptionality] = React.useState(true);
    // undefined = the user has not overridden the markup; calculations fall back to the suggestion's
    // sales-history-derived markup and the input shows a placeholder saying so.
    const [bundleMarkup, setBundleMarkup] = React.useState<number | undefined>(undefined);
    // Mirrors the product-level storefront Always Available toggle, but at the bundle level. Defaults off,
    // reset whenever a different suggestion opens.
    const [alwaysAvailable, setAlwaysAvailable] = React.useState(false);
    // Optional floor for the bundle's storefront price. undefined/blank means unset - never sent as 0.
    const [minimumPrice, setMinimumPrice] = React.useState<number | undefined>(undefined);
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();

    const storefrontScoped = !!props.searchCriteria?.inStorefront && !showAllBundles;
    const retrieveBundle = useGetProductBundleRefsQuery({
        listedInStorefront: storefrontScoped,
        keywords: debouncedKeyword,
        storefronts: storefrontScoped ? props.searchCriteria.storefrontFilter : undefined
    }, {skip: !props.searchCriteria});
    // Unfiltered count so we can tell the user when the search criteria hid everything.
    // When the main query is already unfiltered, RTK Query dedupes this into the same request.
    const allBundles = useGetProductBundleRefsQuery({listedInStorefront: false, keywords: debouncedKeyword}, {skip: !props.searchCriteria});
    const shownCount = retrieveBundle.data?.items.length ?? 0;
    const filteredOutCount = Math.max((allBundles.data?.items.length ?? 0) - shownCount, 0);
    const [createNewBundle] = usePostProductBundleMutation();

    // AI-suggested bundles, appended to the end of the same scroller after the real bundles.
    // The run's stats complete synchronously (drives the shimmer placeholder); naming/pricing is then
    // enriched by an LLM pass in the background (drives the small "AI is naming..." note). Poll while
    // either is in flight, capped so a stalled worker (run stuck pending forever) doesn't poll forever.
    const bundleSuggestionsAvailable = useBundleSuggestionsAvailable();
    const [suggestionsPolling, setSuggestionsPolling] = React.useState(false);
    // Set when the poll cap is reached with the run still unfinished. From then on the UI stops treating
    // the run as in flight (no spinner note, Generate re-enabled); whatever the worker eventually writes
    // shows up on the next natural refetch.
    const [pollCapReached, setPollCapReached] = React.useState(false);
    // Skipped when the entitlement is off, since a disabled server 503s these endpoints rather than
    // returning an empty run - never poll (or even fetch) them in that case.
    const suggestionsRetrieve = useGetBundleSuggestionsQuery(undefined, {
        skip: !bundleSuggestionsAvailable,
        pollingInterval: suggestionsPolling ? 5000 : 0,
    });
    const runStatus = suggestionsRetrieve.data?.status?.toLowerCase();
    const enrichmentStatus = suggestionsRetrieve.data?.enrichmentStatus?.toLowerCase();
    const runReportsActive = runStatus === 'pending' || runStatus === 'processing';
    const runReportsEnriching = enrichmentStatus === 'pending';
    const isRunActive = runReportsActive && !pollCapReached;
    const isEnrichmentPending = runReportsEnriching && !pollCapReached;
    const pollCountRef = useRef(0);
    const pollCapReachedRef = useRef(false);
    const pollingRunIdRef = useRef<string | undefined>();
    // Called when a new run is kicked off, so a previously capped-out run doesn't suppress the new one's
    // progress UI before its id has come back from the server.
    const resetEnrichmentPolling = () => {
        pollCountRef.current = 0;
        pollCapReachedRef.current = false;
        setPollCapReached(false);
    };
    useEffect(() => {
        const runId = suggestionsRetrieve.data?.id;
        if (runId !== pollingRunIdRef.current) {
            pollingRunIdRef.current = runId;
            resetEnrichmentPolling();
        }
        const wantsPolling = runReportsActive || runReportsEnriching;
        if (wantsPolling) {
            pollCountRef.current += 1;
        }
        const cappedOut = pollCountRef.current > MAX_ENRICHMENT_POLLS;
        if (wantsPolling && cappedOut && !pollCapReachedRef.current) {
            pollCapReachedRef.current = true;
            setPollCapReached(true);
            toast.info('Naming and pricing these suggestions is taking longer than expected. They will appear once the AI finishes.', {autoClose: 8000});
        }
        setSuggestionsPolling(wantsPolling && !cappedOut);
        // Re-evaluate on every completed fetch (fulfilledTimeStamp), not on `data` - a stalled worker
        // returns the same status every poll, so `data` wouldn't change under structural sharing and
        // the poll count would never advance.
        // eslint-disable-next-line
    }, [suggestionsRetrieve.fulfilledTimeStamp]);
    const [generateSuggestions, generateSuggestionsResult] = useGenerateBundleSuggestionsMutation();
    const [acceptSuggestion] = useAcceptBundleSuggestionMutation();
    const [dismissSuggestion] = useDismissBundleSuggestionMutation();
    const suggestedBundles = (suggestionsRetrieve.data?.suggestions ?? []).filter(s => s.status === 'suggested');
    // Resolved against every suggestion on the run (not just the suggested ones) so the panel stays put
    // through an accept/dismiss round trip, and re-resolved on every poll so enrichment - a renamed
    // bundle, a context item promoted into the items list - reaches an already-open panel.
    const detailsSuggestion = detailsSuggestionId
        ? suggestionsRetrieve.data?.suggestions?.find(s => s.id === detailsSuggestionId)
        : undefined;
    const generatingSuggestions = generateSuggestionsResult.isLoading || isRunActive;
    const showSuggestedDivider = generatingSuggestions || suggestedBundles.length > 0;

    React.useEffect(() => {
        const handler = setTimeout(() => setDebouncedKeyword(keyword), 500);
        return () => clearTimeout(handler);
    }, [keyword]);

    useEffect(() => {
        if (retrieveBundle.isLoading || retrieveBundle.isFetching){
            setLoading(true);
            props.onLoading?.(true);
        }
        else {
            if(retrieveBundle.error){
                setError('Error retrieving product bundles');
                setLoading(false);
                props.onLoading?.(false);
                props.onResultsCountChange?.(0);
            }
            else if(retrieveBundle.data){
                setLoading(false);
                props.onLoading?.(false);
                props.onResultsCountChange?.(retrieveBundle.data.items.length);
            }
        }
    }, [retrieveBundle.isLoading, retrieveBundle.isFetching]);

    useEffect(() => {
        checkScroll();
        const handleResize = () => checkScroll();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [retrieveBundle.data, suggestedBundles.length, generatingSuggestions, isEnrichmentPending]);

    // Build the editable rows (and reset the pricing controls) whenever the panel opens a suggestion, and
    // rebuild them in place when the background enrichment pass changes the open suggestion - typically by
    // promoting a context item into the bundle. An in-place rebuild leaves the pricing controls alone and
    // keeps whatever the user has already edited on rows that survived it.
    const builtRowsSignatureRef = useRef<string>('');
    useEffect(() => {
        if (!detailsSuggestion) {
            builtRowsSignatureRef.current = '';
            return;
        }
        const signature = rowsSignature(detailsSuggestion);
        if (signature === builtRowsSignatureRef.current) return;
        const isDifferentSuggestion = !builtRowsSignatureRef.current.startsWith(`${detailsSuggestion.id}#`);
        builtRowsSignatureRef.current = signature;
        const suggested = Math.round(detailsSuggestion.suggestedMarkup ?? 0);
        // On an in-place rebuild a bundle markup the user already typed is what new rows should be priced
        // against, not the suggestion's own sales-history markup.
        const bm = isDifferentSuggestion ? suggested : (bundleMarkup ?? suggested);
        if (isDifferentSuggestion) {
            setBundleMarkup(undefined);
            setUseItemMarkup(false);
            setUseGroupedOptionality(true);
            setAlwaysAvailable(false);
            setMinimumPrice(undefined);
        }
        const isResolved = (itemKey: string) => !!detailsSuggestion.items.find(i => i.itemKey === itemKey)?.resolvedProductId;
        setGroupSelections(prev => {
            const selections: Record<string, string> = {};
            (detailsSuggestion.groups ?? []).forEach(group => {
                // Keep the user's pick when the group and their choice both survived the rebuild.
                const previous = isDifferentSuggestion ? undefined : prev[group.id];
                if (previous && group.itemKeys.includes(previous)) {
                    selections[group.id] = previous;
                    return;
                }
                let selected = group.defaultItemKey;
                if (!isResolved(selected)) {
                    selected = group.itemKeys.find(isResolved) ?? selected;
                }
                selections[group.id] = selected;
            });
            return selections;
        });
        const buildRow = (item: IBundleSuggestionItem, isContext: boolean): IEditableBundleItemRow => {
            const resolved = !!item.resolvedProductId;
            // An LLM-promoted item is the bundle's anchor (the model names and prices the bundle around it),
            // so it arrives checked like any suggested item, just tagged optional so it is easy to drop.
            const promoted = item.role === 'promoted';
            const isGroupOption = item.role === 'groupOption';
            return {
                itemKey: item.itemKey,
                isContext,
                groupId: item.groupId,
                displayName: item.resolvedProductName || item.name || item.manufacturerPartNumber || item.itemKey,
                resolved,
                inStock: item.resolvedProductInStock,
                unitCost: item.averageUnitCost,
                unitPrice: item.averageUnitPrice,
                // Group rows have no checkbox - their inclusion is derived from the group's radio selection
                // at accept time, so this just stays false and is never read for them.
                include: resolved && !isContext && !isGroupOption,
                quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
                markup: markupFromMargin(item.marginPercent, bm),
                marginPercent: item.marginPercent,
                optional: !isContext && (promoted || item.optionality === 'optional'),
            };
        };
        setItemRows(prev => {
            const previousRows = new Map<string, IEditableBundleItemRow>(
                isDifferentSuggestion ? [] : prev.map(r => [r.itemKey, r] as [string, IEditableBundleItemRow]));
            // Carry a user's edits forward only when the row is still the same kind of row. An item the
            // enrichment pass promoted out of the context table is a new row as far as the user is
            // concerned, so it takes the default (included) rather than the unchecked state it had while
            // it was context.
            const merge = (row: IEditableBundleItemRow): IEditableBundleItemRow => {
                const previous = previousRows.get(row.itemKey);
                if (!previous || !previous.userModified) return row;
                if (previous.isContext !== row.isContext || previous.groupId !== row.groupId) return row;
                return {
                    ...row,
                    include: previous.include,
                    quantity: previous.quantity,
                    markup: previous.markupEdited ? previous.markup : row.markup,
                    userModified: true,
                    markupEdited: previous.markupEdited,
                };
            };
            return [
                // Group members (role 'groupOption') are included here too - they get their own table (radio
                // control column) rendered by group, but share the same qty/markup editing plumbing as everyone else.
                ...detailsSuggestion.items.map(i => merge(buildRow(i, false))),
                ...(detailsSuggestion.contextItems ?? []).map(i => merge(buildRow(i, true))),
            ];
        });
        // eslint-disable-next-line
    }, [detailsSuggestion]);

    const updateItemRow = (itemKey: string, patch: Partial<IEditableBundleItemRow>) => {
        setItemRows(prev => prev.map(r => r.itemKey === itemKey ? {...r, ...patch, userModified: true} : r));
    };

    const suggestedBundleMarkup = Math.round(detailsSuggestion?.suggestedMarkup ?? 0);
    const effectiveBundleMarkup = bundleMarkup ?? suggestedBundleMarkup;

    const applicableMarkupFor = (row: IEditableBundleItemRow) => useItemMarkup ? row.markup : effectiveBundleMarkup;

    const rowLineTotal = (row: IEditableBundleItemRow): number | undefined => {
        if (row.unitCost != null) {
            return row.unitCost * row.quantity * (1 + applicableMarkupFor(row) / 100);
        }
        if (row.unitPrice != null) {
            return row.unitPrice * row.quantity;
        }
        return undefined;
    };

    const groups = detailsSuggestion?.groups ?? [];
    // Main table = regular items only (no context, no group members - those get their own table per
    // group). Context items keep their own table too. Group member rows still live in itemRows (for their
    // qty/markup state), just filtered out of both of these.
    const mainItemRows = itemRows.filter(r => !r.isContext && !r.groupId);
    const contextItemRows = itemRows.filter(r => r.isContext);

    // Shared row body: control cell content and whether the row counts as "active" (drives the row's
    // dimmed styling and whether qty/markup are editable) are supplied by the caller, so the same cell
    // layout serves both the checkbox-driven regular table and the radio-driven group tables.
    const renderTableRow = (row: IEditableBundleItemRow, control: React.ReactNode, active: boolean) => {
        const applicableMarkup = applicableMarkupFor(row);
        const itemPrice = row.unitCost != null ? row.unitCost * (1 + applicableMarkup / 100) : row.unitPrice;
        const lineTotal = rowLineTotal(row);
        return (
            <tr key={row.itemKey} style={{color: row.resolved ? undefined : '#999', opacity: active ? 1 : 0.6}}>
                <td>{control}</td>
                <td>
                    {row.displayName}
                    {!row.resolved && <span style={{fontStyle: 'italic'}}> (not in catalog)</span>}
                    {row.optional &&
                        <span style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#5b6b7c',
                            background: '#eef2f7',
                            borderRadius: 4,
                            padding: '1px 5px',
                            marginLeft: 6,
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle'
                        }}>
                            Optional
                        </span>
                    }
                </td>
                <td>
                    <TextField
                        type='number'
                        value={String(row.quantity)}
                        disabled={!active}
                        styles={{root: {width: 60}}}
                        onChange={(_, v) => {
                            const n = Math.max(1, Math.round(parseFloat(v || '1')) || 1);
                            updateItemRow(row.itemKey, {quantity: n});
                        }}
                    />
                </td>
                <td>{formatCurrency(row.unitCost)}</td>
                {useItemMarkup &&
                    <td>
                        <TextField
                            type='number'
                            value={String(row.markup)}
                            disabled={!active}
                            styles={{root: {width: 60}}}
                            onChange={(_, v) => {
                                const n = Math.max(0, Math.round(parseFloat(v || '0')) || 0);
                                updateItemRow(row.itemKey, {markup: n, markupEdited: true});
                            }}
                        />
                    </td>
                }
                {useItemMarkup && <td>{formatCurrency(itemPrice)}</td>}
                <td>{formatCurrency(lineTotal)}</td>
                <td>
                    {row.inStock === false &&
                        <span title="No stock" style={{display: 'inline-flex', alignItems: 'center', gap: 4, color: '#b71c1c', fontSize: 11, whiteSpace: 'nowrap'}}>
                            <Icon iconName='Warning' style={{fontSize: 12}} /> No stock
                        </span>
                    }
                </td>
            </tr>
        );
    };

    const renderItemRow = (row: IEditableBundleItemRow) => renderTableRow(
        row,
        <Checkbox
            checked={row.include}
            disabled={!row.resolved}
            onChange={(_, checked) => updateItemRow(row.itemKey, {include: checked === true})}
        />,
        row.include
    );

    const renderItemsTable = (rows: IEditableBundleItemRow[], rowRenderer: (row: IEditableBundleItemRow) => React.ReactElement = renderItemRow) => (
        <div style={{width: '100%', overflowX: 'auto', marginBottom: 16}}>
            <table className='data-table'>
                <thead>
                <tr>
                    <th></th>
                    <th>Name</th>
                    <th>Qty</th>
                    <th>Unit Cost</th>
                    {useItemMarkup && <th>Item Markup %</th>}
                    {useItemMarkup && <th>Item Price</th>}
                    <th>Line Total</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                {rows.map(rowRenderer)}
                </tbody>
            </table>
        </div>
    );

    // One "pick one" group section: same table layout/columns as the regular items table (qty and, in
    // item-markup mode, markup are editable exactly the same way), except the control column is a radio
    // rather than a checkbox, so only one member can be selected. In practice there is at most one group
    // per suggestion.
    const renderGroupSection = (group: IBundleSuggestionGroup) => {
        const memberRows = itemRows
            .filter(r => r.groupId === group.id)
            .sort((a, b) => group.itemKeys.indexOf(a.itemKey) - group.itemKeys.indexOf(b.itemKey));
        const renderGroupRow = (row: IEditableBundleItemRow) => {
            const selected = groupSelections[group.id] === row.itemKey;
            const radio = (
                <input
                    type='radio'
                    name={`bundle-suggestion-group-${group.id}`}
                    checked={selected}
                    disabled={!row.resolved}
                    onChange={() => setGroupSelections(prev => ({...prev, [group.id]: row.itemKey}))}
                    style={{
                        width: 16,
                        height: 16,
                        margin: '0 0 0 3px',
                        accentColor: 'rgb(120, 156, 47)',
                        cursor: row.resolved ? 'pointer' : 'not-allowed'
                    }}
                />
            );
            return renderTableRow(row, radio, selected);
        };
        return (
            <div key={group.id} style={{marginBottom: 16}}>
                <div style={{display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4}}>
                    <h3 style={{margin: 0}}>{group.name}</h3>
                    <span style={{fontSize: 12, color: '#666'}}>(pick one)</span>
                </div>
                {renderItemsTable(memberRows, renderGroupRow)}
                <ToggleField
                    inline={true}
                    label='Use grouped optionality'
                    checked={useGroupedOptionality}
                    toolTip='All options are added to the bundle as a selectable group. If unchecked, only the selected option is added.'
                    onChange={(_, checked) => setUseGroupedOptionality(checked === true)}
                />
            </div>
        );
    };

    // Selected group members contribute exactly like a regular checked row (same rowLineTotal math, so
    // per-item markup applies to them too in item-markup mode) - never all members, only the one picked.
    const selectedGroupRows = groups
        .map(g => itemRows.find(r => r.groupId === g.id && r.itemKey === groupSelections[g.id]))
        .filter((r): r is IEditableBundleItemRow => !!r);

    const estimatedCost = itemRows
        .filter(r => r.include)
        .reduce((sum, r) => sum + (r.unitCost != null ? r.unitCost * r.quantity : 0), 0)
        + selectedGroupRows.reduce((sum, r) => sum + (r.unitCost != null ? r.unitCost * r.quantity : 0), 0);
    const estimatedPrice = itemRows
        .filter(r => r.include)
        .reduce((sum, r) => sum + (rowLineTotal(r) ?? 0), 0)
        + selectedGroupRows.reduce((sum, r) => sum + (rowLineTotal(r) ?? 0), 0);

    const buildAcceptRequestBody = (): IAcceptBundleSuggestionRequest => ({
        useItemMarkup,
        bundleMarkup: effectiveBundleMarkup,
        // Group members are included here too, alongside the regular items - the server disregards
        // include for them (groups below is authoritative for group membership), but their quantity and
        // (in item-markup mode) markup still need to travel with the item, so include is sent truthfully
        // as "is this the group's current selection".
        items: itemRows.map(r => ({
            itemKey: r.itemKey,
            include: r.groupId ? groupSelections[r.groupId] === r.itemKey : r.include,
            quantity: r.quantity,
            ...(useItemMarkup ? {markup: r.markup} : {}),
        })),
        ...(groups.length > 0 ? {
            useGroupedOptionality,
            groups: groups.map(g => ({groupId: g.id, selectedItemKey: groupSelections[g.id]})),
        } : {}),
        ...(alwaysAvailable ? {storefrontAlwaysAvailable: true} : {}),
        ...(minimumPrice != null ? {minimumPrice} : {}),
    });

    // Same deep-link resolution pattern as ProductPurchaseHistory's openOpportunity: try to resolve a
    // live CRM URL and open it, toasting an explanation when the folio doesn't support it or the call fails.
    const openOpportunityRecord = async (folioId: string, recordId: string) => {
        const toastId = toast.loading('Opening record in the CRM…');
        try {
            const res = await resolveRecordUrl({folioId, recordType: RECORD_TYPE_OPPORTUNITY, recordId}).unwrap();
            if (!res.supported) {
                toast.update(toastId, {type: 'error', render: "This record can't be opened in the CRM", isLoading: false, autoClose: 4000});
            } else if (res.success && res.url) {
                window.open(res.url, '_blank', 'noopener');
                toast.dismiss(toastId);
            } else {
                toast.update(toastId, {type: 'error', render: res.error?.trim() || 'Could not open record in the CRM', isLoading: false, autoClose: 5000});
            }
        } catch {
            toast.update(toastId, {type: 'error', render: 'Failed to open record', isLoading: false, autoClose: 4000});
        }
    };

    const createNew =() => {
        setCreating(true)
        setDebouncedKeyword('')
        createNewBundle({request: {name: 'New Bundle'}})
            .unwrap()
            .then((result) => {
                if (result) {
                    const newBundle = {id: result.id, name: 'New Bundle'}
                    props.onCreateNewBundle?.(newBundle);
                    openDetails(newBundle)
                    setCreating(false)
                }
            })
            .catch((err) => {
                console.error('Error creating new bundle:', err);
            });
    }

    // body is omitted for the card's quick-create path (legacy, server picks defaults) and supplied by
    // the details panel's Create Bundle button (the user's edited include/quantity/markup configuration).
    const acceptSuggestedBundle = (suggestion: IBundleSuggestion, body?: IAcceptBundleSuggestionRequest) => {
        if (!suggestionsRetrieve.data?.id) return;
        setAcceptingSuggestionId(suggestion.id);
        const toastId = toast.loading('Creating bundle from suggestion...');
        acceptSuggestion({runId: suggestionsRetrieve.data.id, suggestionId: suggestion.id, body})
            .unwrap()
            .then((result) => {
                toast.update(toastId, {type: 'success', render: 'Bundle created', isLoading: false, autoClose: 3000});
                if (result?.id) {
                    const newBundle = {id: result.id, name: suggestion.name};
                    setDetailsSuggestionId(undefined);
                    props.onCreateNewBundle?.(newBundle);
                    openDetails(newBundle);
                }
            })
            .catch((err) => {
                const detail = err?.data?.detail ?? err?.data?.message;
                toast.update(toastId, {
                    type: 'error',
                    render: detail ? `Failed to create bundle: ${detail}` : 'Failed to create bundle',
                    isLoading: false,
                    autoClose: 4000
                });
                console.error('Error accepting bundle suggestion:', err);
            })
            .finally(() => setAcceptingSuggestionId(undefined));
    }

    const dismissSuggestedBundle = (suggestion: IBundleSuggestion, body?: IDismissBundleSuggestionRequest) => {
        if (!suggestionsRetrieve.data?.id) return;
        setDismissingSuggestionId(suggestion.id);
        dismissSuggestion({runId: suggestionsRetrieve.data.id, suggestionId: suggestion.id, body})
            .unwrap()
            .catch((err) => {
                console.error('Error dismissing bundle suggestion:', err);
            })
            .finally(() => setDismissingSuggestionId(undefined));
    }

    // Confirm handler for the shared dismiss-reason dialog, opened from either the card's X icon or the
    // panel's Dismiss button.
    const confirmDismissSuggestion = (reason?: string, detail?: string) => {
        if (!dismissDialogTarget) return;
        const {suggestion, fromPanel} = dismissDialogTarget;
        dismissSuggestedBundle(suggestion, {reason, detail});
        if (fromPanel) setDetailsSuggestionId(undefined);
        setDismissDialogTarget(undefined);
    }

    // Small thumbnail left of the title, same idea as StorefrontBundleModal's own custom panel header.
    const suggestionPanelHeader = (suggestion: IBundleSuggestion) => (
        <div style={{width: '100%', display: 'flex', alignItems: 'center', gap: 16, paddingLeft: '24px'}}>
            <Image
                src={suggestion.imageUrl || PLACEHOLDER_IMAGE}
                alt={suggestion.name}
                style={{width: 48, height: 48, objectFit: 'contain', flexShrink: 0}}
            />
            <h1 style={{margin: 0}}>{suggestion.name}</h1>
        </div>
    );

    const suggestionPanelFooterButtons = (suggestion: IBundleSuggestion) => {
        const accepting = acceptingSuggestionId === suggestion.id;
        const dismissing = dismissingSuggestionId === suggestion.id;
        return [
            <PrimaryButton
                key="create"
                text={accepting ? 'Creating...' : 'Create Bundle'}
                iconProps={accepting ? undefined : {iconName: 'Add'}}
                onRenderIcon={accepting ? () => <Spinner size={SpinnerSize.xSmall} /> : undefined}
                disabled={accepting || dismissing}
                styles={{root: {width: '100%'}}}
                onClick={() => acceptSuggestedBundle(suggestion, buildAcceptRequestBody())}
            />,
            <DefaultButton
                key="dismiss"
                text="Dismiss"
                disabled={accepting || dismissing}
                styles={{root: {width: '100%'}}}
                onClick={() => {
                    setDismissDialogTarget({suggestion, fromPanel: true});
                }}
            />
        ];
    }

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const cardWidth = 200; // minWidth of card
    const gap = 54; // gap between cards

    const checkScroll = () => {
        const el = scrollContainerRef.current;
        if (el) {
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollLeft + el.clientWidth + 1 < el.scrollWidth);
        }
    };

    const handleScroll = () => checkScroll();


    const scrollByCard = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = cardWidth + gap;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    // Strip controls. Header row (unpinned): close, pin, refresh, suggestions. Pinned stack beside the right
    // chevron: pin, refresh, new bundle, suggestions (the create pod is gone from the compact row).
    const stripControls = (
        <>
                    {!compact &&
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel="Close bundles"
                        title="Close"
                        onClick={() => props.onHide?.()}
                    />}
                    <IconButton
                        iconProps={{ iconName: pinned ? 'Unpin' : 'Pin' }}
                        ariaLabel={pinned ? 'Unpin bundles' : 'Pin bundles to the top'}
                        title={pinned ? 'Unpin: return the bundles to the page' : 'Pin: keep the bundles at the top while you scroll the results'}
                        styles={{root: {color: pinned ? '#789c2f' : undefined}, icon: {color: pinned ? '#789c2f' : undefined}}}
                        onClick={togglePinned}
                    />
                    <IconButton
                        iconProps={{ iconName: 'Refresh' }}
                        ariaLabel="Refresh bundles"
                        title="Refresh"
                        disabled={retrieveBundle.isFetching || allBundles.isFetching}
                        onClick={() => {
                            retrieveBundle.refetch();
                            allBundles.refetch();
                        }}
                    />
                    {compact &&
                    <IconButton
                        iconProps={{ iconName: creating ? 'Sync' : 'Add' }}
                        ariaLabel="Create new bundle"
                        title={creating ? 'Creating...' : 'Create new bundle'}
                        disabled={creating}
                        onClick={createNew}
                    />}
                    {bundleSuggestionsAvailable &&
                    <IconButton
                        iconProps={{ iconName: 'Robot' }}
                        ariaLabel="Generate suggested bundles"
                        title="Generate suggested bundles"
                        disabled={generatingSuggestions}
                        onClick={() => {
                            resetEnrichmentPolling();
                            const toastId = toast.loading('Generating bundle suggestions...');
                            generateSuggestions({maxSuggestions: 10})
                                .unwrap()
                                .then((result) => {
                                    if (result?.success === false) {
                                        toast.update(toastId, {
                                            type: 'error',
                                            render: result.message || 'Failed to generate bundle suggestions',
                                            isLoading: false,
                                            autoClose: 4000
                                        });
                                    } else {
                                        // The API's message distinguishes a run that produced bundles from one
                                        // that legitimately found nothing (or only re-finds of dismissed or
                                        // already-created ones); show its words rather than claiming success
                                        // over an empty strip.
                                        const produced = result?.message === 'Bundle suggestions generated';
                                        toast.update(toastId, {
                                            type: produced ? 'success' : 'info',
                                            render: result?.message || 'Bundle suggestions generated',
                                            isLoading: false,
                                            autoClose: produced ? 3000 : 6000
                                        });
                                    }
                                })
                                .catch((err) => {
                                    toast.update(toastId, {type: 'error', render: 'Failed to generate bundle suggestions', isLoading: false, autoClose: 4000});
                                    console.error('Error generating bundle suggestions:', err);
                                });
                        }}
                    />}
        </>
    );

    return (
        <div>
            {props.showResults &&
                // Pinned: the compact strip is fixed directly under the app header; the page pads its top by the
                // strip's height (reported through onPinnedHeightChange) so the search box sits below it.
                <div ref={stripRef} style={compact ? {
                    position: 'fixed',
                    top: HEADER_HEIGHT,
                    left: 0,
                    right: props.pinnedRightInset ?? 0,
                    zIndex: 12,
                    background: '#f5f6fa',
                    borderRadius: 0,
                    padding: '2px 12px 2px 12px',
                    marginBottom: 0,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                    transition: 'right 0.25s ease',
                } : {
                    position: 'relative',
                    background: '#f5f6fa',
                    borderRadius: 8,
                    padding: 24,
                    marginBottom: 24
                }}>
                <div style={{display: 'flex'}} >
                    {!compact && <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0 }}><Icon iconName={BUNDLE_ICON} styles={{root: {fontSize: 16, marginRight: 8, color: '#789c2f', verticalAlign: 'text-bottom'}}} aria-hidden />Bundles</h3>
                        <TextField
                            placeholder="Search bundles..."
                            value={keyword}
                            onChange={(_, v) => setKeyword(v || '')}
                            styles={{ root: { width: 250 } }}
                        />
                        {/* Selected grid rows into the selected bundle. Disabled (with the reason) until both exist. Hidden while stuck: drop onto a card instead. */}
                        <DefaultButton
                            iconProps={{ iconName: 'Add' }}
                            text={selectedRefs.length > 0 && selectedBundle
                                ? `Add ${selectedRefs.length} selected to ${selectedBundle.name ?? 'bundle'}`
                                : 'Add selected to bundle'}
                            disabled={!selectedBundle || selectedRefs.length === 0 || !!addingToBundleId}
                            title={!selectedBundle
                                ? 'Select a bundle card first'
                                : selectedRefs.length === 0 ? 'Select one or more products in the results first'
                                : `Add the selected products to ${selectedBundle.name ?? 'the bundle'}. You can also drag rows onto a bundle card.`}
                            styles={{root: {whiteSpace: 'nowrap'}, icon: {color: '#789c2f'}}}
                            onClick={() => { if (selectedBundle) void addToBundle(selectedBundle, selectedRefs); }}
                        />
                        {props.searchCriteria?.inStorefront &&
                            <ToggleField
                                inline={true}
                                label="Show All Bundles"
                                checked={showAllBundles}
                                onChange={(_, checked) => setShowAllBundles(checked === true)}
                            />
                        }
                        <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center'}}>
                            {stripControls}
                        </div>
                    </div>}
                </div>
    
                <div style={{display: 'flex', alignItems: 'stretch'}}>
                <div style={{position: 'relative', flexGrow: 1, minWidth: 0, display: compact ? 'flex' : undefined, alignItems: compact ? 'stretch' : undefined}}>
                    {/* Left Arrow */}
                    {/* Stuck row: with a bundle and products selected, the Add tile leads the row, outside the scroll area so it never scrolls away. */}
                    {compact && selectedBundle && selectedRefs.length > 0 && (
            <div
                role='button'
                        title={addingToBundleId ? 'Adding…' : `Add the ${selectedRefs.length} selected product${selectedRefs.length === 1 ? '' : 's'} to ${selectedBundle.name ?? 'the bundle'}`}
                        onClick={() => { if (!addingToBundleId) void addToBundle(selectedBundle, selectedRefs); }}
                        style={{
                            // A tile in the create pod's slot: same footprint, solid brand green, the count and target stacked.
                            width: 96, minWidth: 96, height: 120, boxSizing: 'border-box', margin: '4px 2px', padding: 6, borderRadius: 4,
                            background: addingToBundleId ? '#a8c371' : '#789c2f', color: '#fff', cursor: addingToBundleId ? 'wait' : 'pointer',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, textAlign: 'center', flexShrink: 0,
                        }}
                    >
                        <Icon iconName={addingToBundleId ? 'Sync' : 'Add'} styles={{root: {fontSize: 20}}} />
                        <span style={{fontSize: 13, fontWeight: 700, lineHeight: '16px'}}>Add {selectedRefs.length}</span>
                        <span style={{fontSize: 10, lineHeight: '12px', opacity: 0.9, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>to {selectedBundle.name ?? 'bundle'}</span>
                    </div>
                    )}
                    {canScrollLeft && 
                    <IconButton
                        iconProps={{ iconName: 'ChevronLeft' }}
                        style={{
                            position: compact ? 'static' : 'absolute',
                            left: 0,
                            top: compact ? undefined : '50%',
                            transform: compact ? undefined : 'translateY(-50%)',
                            width: compact ? 36 : 50,
                            flexShrink: 0,
                            height: compact ? 'auto' : 280,
                            zIndex: 1,
                            background: leftHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                        }}
                        onClick={() => scrollByCard('left')}
                        ariaLabel="Scroll left"
                        onMouseEnter={() => setLeftHover(true)}
                        onMouseLeave={() => setLeftHover(false)}
                    />
                    }
                    {/* Scrollable Container */}
        
                    <div
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        className={compact ? 'bundle-strip-compact' : undefined}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            minWidth: 0,
                            flexGrow: 1,
                            // Grows with the tallest card (the cost line can take two rows) so the Details button never spills out.
                            minHeight: compact ? undefined : '300px',
                            justifyContent: 'flex-start',
                            overflowX: 'auto',
                            width: '100%',
                            gap: compact ? '6px' : '10px',
                            scrollBehavior: 'smooth'
                        }}
                    >
                        {/* Create New Pod (not while pinned: the stack beside the chevron has a new-bundle button instead) */}
                        {!compact && <div
                            onMouseEnter={() => setCreateHover(true)}
                            onMouseLeave={() => setCreateHover(false)}
                            onClick={createNew}
                            style={{
                                border: createHover ? '2px dashed rgb(120, 156, 47)' : '2px dashed #ddd',
                                padding: compact ? '4px' : '10px',
                                margin: compact ? '4px 2px' : '10px',
                                height: compact ? 120 : undefined,
                                boxSizing: 'border-box',
                                cursor: 'pointer',
                                // While stuck the pod is a fixed 48px tile (the row is one card tall; expanding here rippled the row).
                                width: compact ? '48px' : createHover ? '200px' : '48px',
                                minWidth: compact ? '48px' : createHover ? '200px' : '48px',
                                maxWidth: compact ? '48px' : createHover ? '200px' : '48px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                background: createHover ? 'rgba(120, 156, 47, 0.05)' : 'transparent',
                                borderRadius: 4,
                                transition: 'width 0.2s, min-width 0.2s, max-width 0.2s, border 0.15s, background 0.15s',
                                flexShrink: 0,
                                overflow: 'hidden',
                            }}
                            title={'Create New Bundle'}
                        >
                            <IconButton
                                iconProps={{ iconName: creating ? 'Sync' : 'Add' }}
                                disabled={creating}
                                styles={{
                                    root: { width: 28, height: 28, pointerEvents: 'none', flexShrink: 0 },
                                    icon: { fontSize: 18, color: createHover ? 'rgb(120, 156, 47)' : '#aaa' }
                                }}
                            />
                            {createHover && !compact && (
                                <span style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: 'rgb(120, 156, 47)',
                                    textAlign: 'center',
                                    whiteSpace: 'nowrap',
                                }}>
                                        {creating ? 'Creating...' : 'Create New Bundle'}
                                    </span>
                            )}
                        </div>}
                        {!loading && shownCount === 0 && filteredOutCount > 0 &&
                            <div style={{ display: 'flex', alignItems: 'center', padding: '0 1em', color: '#666', fontStyle: 'italic' }}>
                                {filteredOutCount} bundle{filteredOutCount === 1 ? '' : 's'} filtered out by search criteria
                            </div>
                        }
                        {retrieveBundle.data?.items.map((item) => {
                            const isSelected = selectedBundle?.id === item.id;
                            const isDropTarget = dragOverBundleId === item.id;
                            const isAdding = addingToBundleId === item.id;
                            const border = isDropTarget ? '3px dashed #789c2f' : isSelected ? `${compact ? 2 : 4}px solid rgb(120, 156, 47)` : '1px solid #ccc';
                            const background = isDropTarget ? 'rgba(120, 156, 47, 0.12)' : isSelected ? 'rgb(220, 237, 200)' : '#fff';
                            const select = () => { setSelectedBundle(item); props.onSelectedBundleChange?.(item); };
                            const dropTitle = 'Drop products here to add them to this bundle';

                            if (compact) {
                                // Mini card: image above name, cost and count. Sized to the control column beside the row
                                // (four icon buttons), so the stuck strip is roughly one control column tall.
                                return (
                                <div
                                    key={item.id}
                                    {...dropHandlers(item)}
                                    className={`bundle-card${isSelected ? ' bundle-card-selected' : ''}`}
                                    title={`${item.name ?? 'Bundle'}. ${dropTitle}; double-click to edit`}
                                    style={{
                                        borderRadius: 5, backgroundColor: background, border, padding: '6px 8px', margin: '4px 2px',
                                        cursor: 'pointer', minWidth: 150, maxWidth: 150, height: 120, flexShrink: 0,
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
                                        opacity: isAdding ? 0.6 : 1, transition: 'border-color 0.15s, background-color 0.15s', boxSizing: 'border-box',
                                    }}
                                    onClick={select}
                                    onDoubleClick={(e) => { e.stopPropagation(); openDetails(item); }}
                                >
                                    <Image src={item.imageUrl && item.imageUrl !== '' ? item.imageUrl : PLACEHOLDER_IMAGE} alt={item.name}
                                           style={{width: '100%', height: 56, objectFit: 'contain', flexShrink: 0}} />
                                    <div style={{width: '100%', minWidth: 0}}>
                                        <div title={item.name} style={{fontSize: 12, fontWeight: 600, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{item.name}</div>
                                        {item.id && <BundleCardExtras bundleId={item.id} name={item.name} onOpen={() => openDetails(item)} compact />}
                                    </div>
                                </div>);
                            }

                            return(
                            <div
                                key={item.id}
                                {...dropHandlers(item)}
                                className={`bundle-card${isSelected ? ' bundle-card-selected' : ''}`}
                                title={dropTitle}
                                style={{
                                    borderRadius: '5px',
                                    backgroundColor: background,
                                    border,
                                    padding: '10px',
                                    margin: '10px',
                                    cursor: 'pointer',
                                    minWidth: '200px',
                                    maxWidth: '200px',
                                    opacity: isAdding ? 0.6 : 1,
                                    transition: 'border-color 0.15s, background-color 0.15s',
                                }}
                                onClick={select}
                                onMouseEnter={e => (e.currentTarget.style.cursor = 'pointer')}
                            >

                                <div style={{
                                    height: '150px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {item.imageUrl && item.imageUrl != '' ?
                                    <Image src={item.imageUrl} alt={item.name}
                                                             style={{width: '80%', height: 'auto', maxHeight: '185px', margin: '0 auto'}}/> :
                                    <Image src={PLACEHOLDER_IMAGE} alt={item.name}
                                           style={{width: '80%', height: 'auto', margin: '0 auto'}}/>
                                    }
                                </div>
                                <div style={{height: '40px', width: '90%', display: 'flex', alignItems: 'center',}}>
                                    <h3 title={item.name} style={{
                                        textAlign: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{item.name}</h3>
                                </div>
                                {/* Same cost range + contents peek as the workspace bundle picker; loads when the card scrolls into view. */}
                                {item.id && <BundleCardExtras bundleId={item.id} name={item.name} onOpen={() => openDetails(item)} />}
                                <PrimaryButton
                                    iconProps={{ iconName: 'Edit' }}
                                    text="Details"
                                    style={{width: '100%'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openDetails(item);
                                    }}
                                />
                            </div>);
                        })}
                        {showSuggestedDivider &&
                            <div style={{position: 'relative', width: 24, margin: '10px 4px', flexShrink: 0}}>
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: 0,
                                    bottom: 0,
                                    width: 2,
                                    background: '#c8c8c8',
                                    transform: 'translateX(-50%)'
                                }} />
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -50%) rotate(180deg)',
                                    writingMode: 'vertical-rl',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: '#666',
                                    background: '#f5f6fa',
                                    padding: '4px 2px',
                                    whiteSpace: 'nowrap'
                                }}>
                                    Suggested Bundles
                                </div>
                            </div>
                        }
                        {showSuggestedDivider &&
                        <div style={{display: 'flex', flexDirection: 'column', flexShrink: 0}}>
                        {isEnrichmentPending && !generatingSuggestions &&
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '8px 10px 0 10px'
                            }}>
                                <Spinner size={SpinnerSize.xSmall} />
                                <span style={{fontSize: 12, color: '#666', whiteSpace: 'nowrap'}}>AI is naming and pricing these suggestions...</span>
                            </div>
                        }
                        <div style={{display: 'flex', flex: 1}}>
                        {generatingSuggestions &&
                            <div style={{
                                borderRadius: '5px',
                                border: '2px dashed #9aa4b0',
                                padding: '10px',
                                margin: '10px',
                                minWidth: '200px',
                                maxWidth: '200px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 12
                            }}>
                                <Spinner size={SpinnerSize.medium} />
                                <span style={{fontSize: 13, color: '#666', textAlign: 'center'}}>Generating suggestions...</span>
                            </div>
                        }
                        {!generatingSuggestions && suggestedBundles.map((suggestion) => {
                            const accepting = acceptingSuggestionId === suggestion.id;
                            const dismissing = dismissingSuggestionId === suggestion.id;
                            const itemCountText = `${suggestion.items.length} item${suggestion.items.length === 1 ? '' : 's'}`;
                            const markupText = suggestion.suggestedMarkup != null ? `${suggestion.suggestedMarkup}% markup` : undefined;
                            const hasImage = !!suggestion.imageUrl;
                            // Shared "Suggested" chip, absolutely positioned at the top-left of whichever
                            // zone it's dropped into (the image area when there's an image, the centered
                            // title zone when there isn't) - both are position: relative.
                            const suggestedChip = (
                                <div style={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    background: 'rgba(255,255,255,0.9)',
                                    borderRadius: 4,
                                    padding: '2px 6px'
                                }}>
                                    <Icon iconName='Robot' style={{fontSize: 12, color: '#5b6b7c'}} />
                                    <span style={{fontSize: 11, fontWeight: 600, color: '#5b6b7c'}}>Suggested</span>
                                </div>
                            );
                            return (
                                <div
                                    key={suggestion.id}
                                    style={{
                                        position: 'relative',
                                        borderRadius: '5px',
                                        backgroundColor: '#fff',
                                        border: '2px dashed #9aa4b0',
                                        padding: '10px',
                                        margin: '10px',
                                        cursor: 'pointer',
                                        minWidth: '200px',
                                        maxWidth: '200px',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                    onClick={() => setDetailsSuggestionId(suggestion.id)}
                                >
                                    <IconButton
                                        iconProps={{ iconName: 'Cancel' }}
                                        ariaLabel="Dismiss suggestion"
                                        title="Dismiss"
                                        disabled={dismissing}
                                        styles={{
                                            root: {
                                                position: 'absolute', top: 2, right: 2, width: 24, height: 24,
                                                background: 'rgba(255,255,255,0.85)', borderRadius: '50%'
                                            }
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDismissDialogTarget({suggestion, fromPanel: false});
                                        }}
                                    />
                                    {hasImage ?
                                        <>
                                            <div style={{
                                                position: 'relative',
                                                height: 84,
                                                flexShrink: 0,
                                                marginBottom: 6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden'
                                            }}>
                                                <Image
                                                    src={suggestion.imageUrl || PLACEHOLDER_IMAGE}
                                                    alt={suggestion.name}
                                                    style={{maxWidth: '80%', maxHeight: '100%', objectFit: 'contain', margin: '0 auto'}}
                                                />
                                                {suggestedChip}
                                            </div>
                                            <div style={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                gap: 4
                                            }}>
                                                <div style={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>
                                                    {suggestion.name}
                                                </div>
                                                <div style={{fontSize: 11, color: '#666', textAlign: 'center'}}>
                                                    {itemCountText}{markupText ? ` | ${markupText}` : ''}
                                                </div>
                                                {suggestion.estimatedPrice != null &&
                                                    <div style={{fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center'}}>
                                                        {formatCurrency(suggestion.estimatedPrice)} est.
                                                    </div>
                                                }
                                            </div>
                                        </>
                                    :
                                        <>
                                            {/* No image: skip the placeholder graphic entirely and center the title in the
                                                combined room the image area + title line would have occupied. The chip
                                                keeps its usual top-left position (this zone is now the position:relative
                                                anchor); meta lines and the button stay put at the card's bottom. */}
                                            <div style={{
                                                position: 'relative',
                                                flex: 1,
                                                marginBottom: 6,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden'
                                            }}>
                                                {suggestedChip}
                                                <div style={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    textAlign: 'center',
                                                    padding: '0 8px',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 4,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}>
                                                    {suggestion.name}
                                                </div>
                                            </div>
                                            <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                                                <div style={{fontSize: 11, color: '#666', textAlign: 'center'}}>
                                                    {itemCountText}{markupText ? ` | ${markupText}` : ''}
                                                </div>
                                                {suggestion.estimatedPrice != null &&
                                                    <div style={{fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center'}}>
                                                        {formatCurrency(suggestion.estimatedPrice)} est.
                                                    </div>
                                                }
                                            </div>
                                        </>
                                    }
                                    <PrimaryButton
                                        text={accepting ? 'Creating...' : 'Create Bundle'}
                                        onRenderIcon={accepting ? () => <Spinner size={SpinnerSize.xSmall} /> : undefined}
                                        iconProps={accepting ? undefined : {iconName: 'Add'}}
                                        disabled={accepting}
                                        style={{width: '100%'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            acceptSuggestedBundle(suggestion);
                                        }}
                                    />
                                </div>
                            );
                        })}
                        </div>
                        </div>
                        }
                    </div>
                    {/* Right Arrow */}
                    {canScrollRight && 
                    <IconButton
                        iconProps={{ iconName: 'ChevronRight' }}
                        style={{
                            position: compact ? 'static' : 'absolute',
                            right: 0,
                            top: compact ? undefined : '50%',
                            transform: compact ? undefined : 'translateY(-50%)',
                            width: compact ? 36 : 50,
                            flexShrink: 0,
                            height: compact ? 'auto' : 280,
                            zIndex: 1,
                            background: rightHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                        }}
                        onClick={() => scrollByCard('right')}
                        ariaLabel="Scroll right"
                        onMouseEnter={() => setRightHover(true)}
                        onMouseLeave={() => setRightHover(false)}
                    />
                    }
                </div>
                {/* While pinned, the strip controls sit in a column beside the right chevron, clear of the cards. */}
                {compact && (
                <div style={{display: 'flex', flexDirection: 'column', gap: 2, marginLeft: 6, justifyContent: 'center'}}>
                    {stripControls}
                </div>
                )}
                </div>
                {detailsBundle?.id &&
                    <StorefrontBundleModal
                        bundleId={detailsBundle.id}
                        isOpen={true}
                        onDismiss={() => setDetailsBundle(undefined)}
                    />
                }
                {detailsSuggestion &&
                    <PanelWrapper
                        isOpen={true}
                        onDismiss={() => setDetailsSuggestionId(undefined)}
                        title={detailsSuggestion.name}
                        onRenderHeader={() => suggestionPanelHeader(detailsSuggestion)}
                        type={useItemMarkup ? PanelType.custom : PanelType.medium}
                        customWidthPx={useItemMarkup ? 800 : undefined}
                        isFooterAtBottom={true}
                        footerTop={
                            <div style={{width: '100%', padding: '0 24px', boxSizing: 'border-box'}}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'baseline',
                                    gap: 12,
                                    paddingBottom: 10,
                                    fontSize: 14
                                }}>
                                    <span>Estimated Cost: <strong>{formatCurrency(estimatedCost)}</strong></span>
                                    <span style={{color: '#c8c8c8'}}>|</span>
                                    <span>Estimated Price: <strong>{formatCurrency(estimatedPrice)}</strong></span>
                                </div>
                                <div style={{display: 'flex', gap: 10}}>
                                    {suggestionPanelFooterButtons(detailsSuggestion).map((button, index) =>
                                        <div key={index} style={{flex: 1}}>{button}</div>
                                    )}
                                </div>
                            </div>
                        }
                    >
                        <div style={{padding: '8px 4px'}}>
                            {detailsSuggestion.description &&
                                <p style={{fontSize: 15, lineHeight: 1.4, margin: '0 0 8px 0'}}>{detailsSuggestion.description}</p>
                            }
                            {detailsSuggestion.rationale &&
                                <p style={{fontSize: 12, fontStyle: 'italic', color: '#666', margin: '0 0 8px 0'}}>{detailsSuggestion.rationale}</p>
                            }
                            {(() => {
                                const runSourceNames = suggestionsRetrieve.data?.sourceNames;
                                const sourceNames = runSourceNames && runSourceNames.length > 0
                                    ? runSourceNames.join(', ')
                                    : undefined;
                                const coOrderCount = detailsSuggestion.evidence?.coOrderCount;
                                let blurb = 'Suggested from your sales history';
                                if (sourceNames) blurb += ` in ${sourceNames}`;
                                blurb += coOrderCount != null
                                    ? ` - this combination of items appeared together on ${coOrderCount} past opportunit${coOrderCount === 1 ? 'y' : 'ies'}.`
                                    : '.';
                                const sampleOrders = detailsSuggestion.evidence?.sampleOrders;
                                const sampleOrderNames = detailsSuggestion.evidence?.sampleOrderNames;
                                return (
                                    <div style={{marginBottom: 16}}>
                                        <p style={{fontSize: 13, color: '#666', margin: '0 0 8px 0'}}>{blurb}</p>
                                        {sampleOrders && sampleOrders.length > 0 &&
                                            <div style={{fontSize: 12, color: '#666'}}>
                                                <strong>Sample opportunities</strong>
                                                <ul style={{margin: '4px 0', paddingLeft: 18}}>
                                                    {sampleOrders.map((o, i) => (
                                                        <li key={o.id ?? i}>
                                                            {o.folioId && o.id
                                                                ? <Link onClick={() => { void openOpportunityRecord(o.folioId as string, o.id); }}>{o.name || o.id}</Link>
                                                                : <span>{o.name || o.id}</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        }
                                        {(!sampleOrders || sampleOrders.length === 0) && sampleOrderNames && sampleOrderNames.length > 0 &&
                                            <div style={{fontSize: 12, color: '#666'}}>
                                                <strong>Sample opportunities</strong>
                                                <ul style={{margin: '4px 0', paddingLeft: 18}}>
                                                    {sampleOrderNames.map((n, i) => <li key={i}>{n}</li>)}
                                                </ul>
                                            </div>
                                        }
                                    </div>
                                );
                            })()}

                            {groups.map(renderGroupSection)}
                            <h3 style={{margin: '0 0 8px 0'}}>Items</h3>
                            {renderItemsTable(mainItemRows)}
                            {contextItemRows.length > 0 && renderItemsTable(contextItemRows)}

                            <div style={{marginBottom: 16}}>
                                <h3 style={{margin: '0 0 8px 0'}}>Pricing</h3>
                                <ToggleField
                                    inline={true}
                                    label='Set individual item markup'
                                    checked={useItemMarkup}
                                    toolTip={`If not set, the bundle level markup will be set to ${effectiveBundleMarkup}%`}
                                    onChange={(_, checked) => {
                                        const on = checked === true;
                                        // Switching into per-item mode reseeds each row off whatever markup the
                                        // bundle-level box currently holds, so a value the user just typed carries
                                        // into item mode instead of being dropped for the suggestion's original
                                        // one. Rows whose markup the user typed themselves are left alone, and the
                                        // bundle-level value stays in state so switching back restores it.
                                        if (on) {
                                            setItemRows(prev => prev.map(r => r.markupEdited
                                                ? r
                                                : {...r, markup: markupFromMargin(r.marginPercent, effectiveBundleMarkup)}));
                                        }
                                        setUseItemMarkup(on);
                                    }}
                                />
                                <div style={{marginTop: 8}}>
                                    <TextField
                                        label='Markup %'
                                        type='number'
                                        value={bundleMarkup == null ? '' : String(bundleMarkup)}
                                        placeholder={String(suggestedBundleMarkup)}
                                        disabled={useItemMarkup}
                                        styles={{root: {width: 160}}}
                                        onChange={(_, v) => {
                                            if (!v || v.trim() === '') {
                                                setBundleMarkup(undefined);
                                                return;
                                            }
                                            const parsed = Math.round(parseFloat(v));
                                            setBundleMarkup(isNaN(parsed) ? undefined : Math.max(0, parsed));
                                        }}
                                    />
                                    {bundleMarkup == null &&
                                        <div style={{fontSize: 11, color: '#666', marginTop: 4}}>
                                            Based on sales history average margin
                                        </div>
                                    }
                                </div>
                                <div style={{marginTop: 8}}>
                                    <ToggleField
                                        inline={true}
                                        label='Always Available'
                                        checked={alwaysAvailable}
                                        toolTip={<div>This forces Adaptive Catalog to include this bundle in the storefront, even if it is not otherwise considered in stock.<br></br><br></br> <b>When enabling this option, it is highly recommended that you set a minimum price, as out of stock items may not include a cost to mark up or may have out of date costs. </b></div>}
                                        onChange={(_, checked) => setAlwaysAvailable(checked === true)}
                                    />
                                </div>
                                <div style={{marginTop: 8}}>
                                    <TextField
                                        label='Minimum Price'
                                        type='number'
                                        value={minimumPrice == null ? '' : String(minimumPrice)}
                                        styles={{root: {width: 160}}}
                                        onChange={(_, v) => {
                                            if (!v || v.trim() === '') {
                                                setMinimumPrice(undefined);
                                                return;
                                            }
                                            const parsed = parseFloat(v);
                                            if (isNaN(parsed)) {
                                                setMinimumPrice(undefined);
                                                return;
                                            }
                                            // Clamp to at most 2 decimal places.
                                            setMinimumPrice(Math.max(0, Math.round(parsed * 100) / 100));
                                        }}
                                    />
                                    <div style={{fontSize: 11, color: '#666', marginTop: 4}}>
                                        Optional floor for the bundle's storefront price
                                    </div>
                                </div>
                            </div>

                            <div style={{fontSize: 11, color: '#a19f9d', fontStyle: 'italic', marginTop: 16, userSelect: 'text'}}>
                                AI ID: {suggestionsRetrieve.data?.id} / {detailsSuggestion.id}
                            </div>
                        </div>
                    </PanelWrapper>
                }
                {dismissDialogTarget &&
                    <DismissBundleSuggestionDialog
                        onCancel={() => setDismissDialogTarget(undefined)}
                        onConfirm={confirmDismissSuggestion}
                    />
                }
            </div>}
        </div>
    );
}

export default ProductBundleSearchTable;